import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useUser from '@hooks/user/use-user';
import {
  getUserBillingAddress,
  getUserTaxInformation,
  saveUserTaxInformation,
  updateUserTaxInformation,
} from '@api/seller-info';
import { IFlattenedSellerInformation, SellerInfoFormPayload } from '@interfaces/models/seller-info/seller-information';
import { SellerInformationKeys } from '@enums/react-query-keys';
import logger from '@helpers/utils/logger/client';
import { flattenObject } from '@helpers/seller-info-helpers';

const useSellerTaxInformation = () => {
  const { user, isAuthenticated } = useUser();
  const queryClient = useQueryClient();

  const { data: taxInformation, isFetching: taxInformationIsFetching } = useQuery<IFlattenedSellerInformation | null>({
    queryKey: [SellerInformationKeys.GET_TAX_INFORMATION, user?.id],
    queryFn: async () => {
      try {
        const data = await getUserTaxInformation();
        const flattenedData: IFlattenedSellerInformation = flattenObject(data);
        flattenedData.country = flattenedData.country;
        flattenedData.street = flattenedData.street;
        queryClient.setQueryData([SellerInformationKeys.GET_TAX_INFORMATION, user?.id], flattenedData);
        return flattenedData || null;
      } catch (error) {
        logger.error(error, 'Error fetching tax information!');
        return null;
      }
    },
    enabled: !!isAuthenticated,
  });

  const { data: billingAddress, isFetching: billingAddressIsFetching } = useQuery<IFlattenedSellerInformation | null>({
    queryKey: [SellerInformationKeys.GET_BILLING_ADDRESS, user?.id],
    queryFn: async () => {
      try {
        const data = await getUserBillingAddress();
        const flattenedData: IFlattenedSellerInformation = flattenObject(data);
        flattenedData.country = flattenedData.iso2;
        flattenedData.street = flattenedData.label;
        queryClient.setQueryData([SellerInformationKeys.GET_BILLING_ADDRESS, user?.id], flattenedData);
        return flattenedData || null;
      } catch (error) {
        logger.error(error, 'Error fetching billing information!');
        return null;
      }
    },
    enabled: isAuthenticated && !taxInformationIsFetching && !taxInformation,
  });

  const prefetchedTaxInformation: IFlattenedSellerInformation = queryClient.getQueryData([
    SellerInformationKeys.GET_TAX_INFORMATION,
    user?.id,
  ]);

  const { mutateAsync: updatedTaxInformation } = useMutation({
    mutationKey: [SellerInformationKeys.UPDATE_TAX_INFORMATION],
    mutationFn: async (taxInformationData: SellerInfoFormPayload) => {
      return (await !prefetchedTaxInformation)
        ? saveUserTaxInformation(taxInformationData)
        : updateUserTaxInformation(taxInformationData);
    },
  });

  const isLoading = billingAddressIsFetching || taxInformationIsFetching;

  return {
    updatedTaxInformation,
    billingAddress,
    taxInformation,
    isLoading,
  };
};

export default useSellerTaxInformation;
